<template>
  <q-page
    ref="seatChartPage"
    class="page column no-wrap items-stretch fixed-full justify-between"
    style="
      margin-left: auto;
      margin-right: auto;
      min-height: unset;
      width: 100%;
    "
    :style="{
      'margin-bottom': $kiosk || $isLandscapeView() ? '0px' : '70px',
      'margin-top': topMargin,
      'max-width': $isLandscapeView() ?`${pageMaxWidth}px` : undefined
    }"
  >
    <div
      v-if="showing.loading"
    >
      <q-spinner
        class="fixed-center"
        size="5.5em"
      />
    </div>
    <component
      :is="showFullScreen ? 'q-dialog' : 'div'"
      v-else
      ref="seatChartContainer"
      v-model="showFullScreen"
      class="relative-position col column no-wrap items-stretch"
      :class="showFullScreen ? 'default-background' : 'q-px-sm'"
      maximized
      :transition-show="$q.screen.lt.sm ? 'slide-up' : undefined"
      :transition-hide="$q.screen.lt.sm ? 'slide-down' : undefined"
    >
      <legend-seats
        ref="seatChartLegend"
        class="bg-transparent col-auto full-width"
        :class="showFullScreen && isIosMobileApp ? 'q-pt-lg' : 'q-pt-xs'"
        :seat-chart-configuration="seatChartForShowing.seatChartConfiguration"
        :initial-selected-seats="initialSelectedSeats"
        :seat-chart="seatChartForShowing"
        :seat-preferences="seatPreferences"
      />

      <template v-if="currentScale === initialScale && showPinchZoomAnimation">
        <pinch-zoom-animation
          v-if="$q.platform.has.touch || $kiosk"
        />
        <scroll-zoom-animation
          v-else
        />
      </template>

      <pinch-scroll-zoom
        v-if="!seatChartForShowing.loading"
        ref="zoomer"
        class="bg-transparent"
        :class="showFullScreen ? 'overflow-hidden' : 'col full-width'"
        key-actions
        key-controls
        :within="false"
        :min-scale="initialMinScale"
        :max-scale="maxScale"
        :scale="initialScale"
        :origin-x="originX"
        :origin-y="originY"
        :wheel-velocity="0.3"
        @scaling="e => onEvent('scaling', e)"
        @stop-drag="e => onEvent('stopDrag', e)"
      >
        <seat-chart
          ref="seatChart"
          :screen-name="showing.screen.name"
          :seat-chart="seatChartForShowing"
          :seat-chart-configuration="seatChartForShowing.seatChartConfiguration"
          :show-seat-numbers="currentSite.consumerShowReservedSeatLabels"
          :hide-unavailable-seat-numbers="true"
          :seats-to-select="seatsToSelect"
          :has-marathon="showing && showing.hasMarathon"
          :ticket-type-id="ticketTypeId"
          :initial-selected-seats="initialSelectedSeats"
          :seat-preferences="seatPreferences"
          :show-footer="false"
          :show-header="false"
          :is-full-screen="showFullScreen"
          :container-width="showFullScreen && $q.screen.gt.xs ? containerWidth : pageMaxWidth"
          @update:model-value="(input) => handleSeatChartInput(input)"
        />
      </pinch-scroll-zoom>

      <q-inner-loading :showing="seatChartForShowing.loading" />
      <div
        class="row items-end"
        style="margin-top: auto; max-height: 80px; top: auto !important;"

        :class=" {
          'q-pa-xs justify-between no-pointer-events': showFullScreen,
          'q-px-xs justify-end': !showFullScreen,
          'fixed-bottom': $q.screen.gt.sm && !$kiosk,
          'absolute-bottom': $q.screen.lt.sm || $kiosk
        }"
      >
        <div
          v-if="selectedSeats && selectedSeats.length > 0 && showFullScreen"
          class="col-auto text-center bg-responsive q-py-xs q-px-sm border-top border-left border-bottom border-right"
        >
          <div
            class="text-left"
            style="word-break: break-all; max-width: 50vw;"
          >
            {{ $t('checkout.steps.seats') }} : <span class="text-h6 text-responsive">{{ selectedSeats.join(', ') }}</span>
          </div>
        </div>
        <q-space
          v-else
          class="col"
        />
        <q-btn-group
          class="q-gutter-sm col-auto all-pointer-events"
          flat
        >
          <q-btn
            class="border-responsive-grey-1 q-pa-sm"
            color="responsive"
            dense
            text-color="responsive"
            icon="fas fa-minus"
            :disabled="initialScale === currentScale"
            @click="onZoomOut()"
          />
          <q-btn
            class="border-responsive-grey-1 q-pa-sm"
            color="responsive"
            dense
            text-color="responsive"
            icon="fas fa-plus"
            :disabled="currentScale >= maxScale"
            @click="onZoomIn()"
          />
          <q-btn
            class="border-responsive-grey-1 q-pa-sm"
            color="responsive"
            dense
            text-color="responsive"
            :icon="showFullScreen ? 'fal fa-minimize' : 'fas fa-expand'"
            @click="toggleFullScreen(selectedSeats)"
          />
        </q-btn-group>
      </div>
    </component>

    <checkout-controls
      v-if="!$q.screen.lt.sm && !hideCheckoutControls && $register"
      @next="next"
    />
  </q-page>
</template>

<script>
import { Cookies } from 'quasar'
import LegendSeats from 'src/modules/admin/seat-chart/LegendSeats'
import PinchZoomAnimation from 'src/modules/admin/seat-chart/PinchZoomAnimation'
import ScrollZoomAnimation from 'src/modules/admin/seat-chart/ScrollZoomAnimation'
import SeatChart from 'src/modules/admin/seat-chart/SeatChart'
import SeatChartMixin from 'src/modules/admin/seat-chart/SeatChartMixin'
import CheckoutControls from 'src/modules/checkout/components/CheckoutControls'
import { watchEffect } from 'vue'
const sortAlphaNum = (a, b) => a.localeCompare(b, 'en', { numeric: true })

export default {
  name: 'Seats',
  components: {
    SeatChart,
    CheckoutControls,
    LegendSeats,
    PinchZoomAnimation,
    ScrollZoomAnimation
  },
  mixins: [SeatChartMixin],
  inject: ['order', 'checkoutData'],
  props: {
    emitNext: {
      type: Boolean,
      default: false,
      required: false
    },
    hideCheckoutControls: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  emits: ['next'],
  data () {
    return {
      saving: false,
      ticket: null,
      loading: false,
      showFullScreen: false,
      initialSelectedSeats: [],
      pageMaxWidth: 700,
      widowWidth: window.innerWidth,
      currentScale: null,
      showPinchZoomAnimation: false
    }
  },
  context: ['currentRegisterClient'],
  configuration: ['currentCircuit', 'currentSite'],
  models: {
    showing: {
      movie: 'default',
      screen: 'default',
      showingBadges: true,
      variables () {
        return {
          id: this.showingId,
          skip: !this.showingId
        }
      },
      immediate: true
    },
    seatChartForShowing: {
      variables () {
        return {
          showingId: this.showingId,
          orderId: this.order?.isRemote ? this.order.id : null,
          skip: !this.showingId
        }
      },
      immediate: true,
      force: true
    }
  },
  computed: {
    isIosMobileApp () {
      return !!window._cordovaNative
    },
    ticketTypeId () {
      if (!this.ticket) {
        return false // This will show all seats
      } else if (this.ticket.ticketType.appliesToSpecificSeats) {
        return this.ticket.ticketType.id
      } else {
        return null // This will only show seats that aren't set to a specific ticket type
      }
    },
    ticketTypeIds () {
      if (this.ticketTypeId === false) return false

      if (!this.ticketTypeId || !this.seatChartForShowing) return null

      // The the smallest subset of ticket type ids all sets of seats with this id has
      // Do this because this is the unit we are selecting over
      const matchingTicketTypeIds = _(this.seatChartForShowing.seatChart)
        .flatten()
        .filter(seat => seat.limitTicketTypes && _.includes(seat.ticketTypeIds, this.ticketTypeId))
        .sort(seat => seat.ticketTypeIds.length)
        .value()

      return matchingTicketTypeIds.length ? matchingTicketTypeIds[0].ticketTypeIds : null
    },
    showingId () {
      if (this.order.ticketForShowing(this.$route.params?.showingId)) return this.$route.params.showingId

      return this.order.firstOrderItemNeedingSeatSelection()?.ticket?.showing?.id ||
        this.order.lastOrderItemWithSeatSelected()?.ticket?.showing?.id ||
        this.$route.params?.showingId
    },
    seatsToSelect () { return this.order && this.order.countSeatsToSelect(this.showing, this.ticketTypeIds) },

    selectedSeatsString () { return this.selectedSeats.join(', ') },
    seatChart () {
      return this.showing?.screen?.seatChart || [[]]
    },
    seatPreferences () {
      return this.order?.user?.seatPreferences || { y_ratio: 0.5 }
    },
    isTicketPrintingDisabled () {
      return this.currentRegisterClient?.registerStation?.registerType?.kioskOptions?.disable_ticket_printing
    },
    topMargin () {
      if (this.$kiosk) {
        if (this.isTicketPrintingDisabled) {
          return this.$isLandscapeView() ? '130px' : '50px'
        } else {
          return this.$isLandscapeView() ? '80px' : undefined
        }
      } else {
        if (this.$q.screen.gt.xs) return '80px'
        if (window._cordovaNative) return '200px'
        return '150px'
      }
    }
  },
  watch: {
    async 'seatChartForShowing.id' () {
      this.showing.screen.seatChart = this.seatChartForShowing

      const columnCount = this.seatChartForShowing.seatChart[0].length

      if (this.$q.screen.gt.xs) {
        this.autoZoom = this.$q.screen.gt.md ? Math.round(columnCount / 14) : Math.round(columnCount / 8)
      } else {
        this.autoZoom = Math.round(columnCount / 6)
      }
      this.maxScale = this.autoZoom + 0.5
      const seatChartCookies = Cookies.get(this.$constants.cookies.seatChartCookies)
      const seatChartCookiesSiteId = seatChartCookies?.siteId
      const hasSeatChartCookies = !!seatChartCookies && seatChartCookiesSiteId === this.currentSite.id
      if (!hasSeatChartCookies) {
        if (this.seatChartForShowing.loading) return

        this.showPinchZoomAnimation = true
        setTimeout(() => {
          this.showPinchZoomAnimation = false
          Cookies.set(this.$constants.cookies.seatChartCookies, { siteId: this.currentSite.id, showPinchZoomAnimation: true, type: this.$constants.cookies.type.consumerConsent, expires: 365 })
        }, 4000)
      }
      await this.$nextTick()
    }
  },
  created () {
    // The only way to get to checkout seat select step without tickets is
    // to have refreshed the page. We currently don't handle re-fetching
    // all required data correctly on refresh, so this is a stopgap solution
    // to prevent the checkout page from breaking.
    if (!this.order?.ticketOrderItems?.length) {
      this.$router.back()
    }
  },
  mounted () {
    this.initializeChart()
    this.getInitialSelectedSeats()
  },
  activated () {
    this.initializeChart()
    this.getInitialSelectedSeats()
  },
  unmounted () {
    this.stopWatch && this.stopWatch()
  },
  methods: {
    async initializeChart () {
      this.stopWatch = watchEffect(() => {
        this.checkoutData.showLoading = false
        this.checkoutData.purchasingMembership = false

        this.checkoutData.stepTitle = this.$i18n.t('checkout.steps.seats')
        this.checkoutData.showMovieCard = true
        this.checkoutData.showCartControls = true
        this.checkoutData.showCheckoutFooter = true

        this.checkoutData.expandDefault = false
        this.checkoutData.expandConcessions = false
        this.checkoutData.expandTickets = true

        this.checkoutData.infoTitle = this.$i18n.t('checkout.infoTitles.seatsSelected')
        this.checkoutData.info = this.selectionDescription
        this.checkoutData.selectedSeats = this.selectedSeatsString
        this.checkoutData.showSubtotal = true
        this.checkoutData.showTotals = true

        this.checkoutData.showEmptyCartNextLabel = false

        this.checkoutData.canProceed = this.selectionComplete && !this.saving
      })

      await this.$nextTick()
      await this.showing.fetch(true)
      this.order.currentShowing = this.showing

      const ticketId = +this.$route.query?.ticketId
      const ticket = ticketId && _.find(this.order.showingTicketOrderItems(this.showing), orderItem => orderItem?.ticket?.id === ticketId)?.ticket

      if (ticket) {
        this.ticket = ticket
      } else {
        // Auto select the type of seat to select from the list remaining
        let firstOrderItemRequiringSeatSelection = null
        let firstOrderItemNotRequiringSeatSelection = null
        _.each(this.order.showingTicketOrderItems(this.showing), orderItem => {
          if (!orderItem.ticket?.seatName) {
            firstOrderItemRequiringSeatSelection ||= orderItem
          } else if (orderItem.ticket?.seatName) {
            firstOrderItemNotRequiringSeatSelection ||= orderItem
          }
        })

        if (firstOrderItemRequiringSeatSelection) {
          this.ticket = firstOrderItemRequiringSeatSelection.ticket
        } else if (firstOrderItemNotRequiringSeatSelection) {
          this.ticket = firstOrderItemNotRequiringSeatSelection.ticket
        }
      }

      await this.$nextTick()
      await this.seatChartForShowing.fetch(true)

      // Run auto select routine
      if (this.currentSite.autoSelectSeats && this.$refs.seatChart) {
        this.$refs.seatChart.autoSelect()
      }

      this.resizeChart()
    },
    getInitialSelectedSeats () {
      this.initialSelectedSeats = this.order && this.order.currentSelectedSeats(this.showing, this.ticketTypeIds)
    },
    async resizeChart () {
      await this.$nextTick()
      const seatChartContainer = this.$refs.seatChartContainer
      const seatChart = this.$refs.seatChart

      if (!seatChart || !seatChartContainer) return

      const heightWithControls = this.showFullScreen
        ? window.innerHeight - 200
        : seatChartContainer.clientHeight - 150
      const widthWithControl = this.showFullScreen
        ? window.innerWidth - 20
        : seatChartContainer.clientWidth

      const heightRatio = heightWithControls / seatChart.$refs['seat-chart'].clientHeight
      const widthRatio = widthWithControl / seatChart.$refs['seat-chart'].clientWidth
      let widthToFillHeight = heightRatio * widthWithControl

      // Fill screen on small phones and scroll
      const minWidth = Math.min(window.visualViewport.width, 400)
      widthToFillHeight = Math.max(widthToFillHeight, minWidth)

      const maxWidth = this.$isLandscapeView() ? window.visualViewport.width - 800 : window.visualViewport.width
      widthToFillHeight = Math.min(widthToFillHeight, maxWidth)

      if (widthToFillHeight > widthWithControl) {
        this.pageMaxWidth = widthToFillHeight
      } else if (widthToFillHeight < widthWithControl && window.visualViewport.width < window.visualViewport.height) {
        this.pageMaxWidth = Math.min(widthToFillHeight, this.$el.scrollWidth)
      } else {
        this.pageMaxWidth = Math.min(window.innerWidth, 700)
      }

      if (heightRatio < 1 && (this.$isLandscapeView() || this.showFullScreenn)) {
        this.initialScale = heightRatio
        this.originX = widthWithControl / 2
        this.originY = 0
      } else if (widthRatio < 1 && this.showFullScreen) {
        this.initialScale = widthRatio
        this.originX = 0
        this.originY = heightWithControls / 2
      } else {
        this.initialScale = 1
        this.originX = 0
        this.originY = 0
      }
      this.currentScale = this.initialScale
      this.containerWidth = widthWithControl
      this.initialMinScale = Math.max(this.initialScale - 0.3, 0.1)
    },
    toggleFullScreen (seats) {
      this.initialSelectedSeats = seats
      this.showFullScreen = !this.showFullScreen
      if (this.showFullScreen) {
        this.maxScale = this.autoZoom / 2 + 0.5
      } else {
        this.maxScale = this.autoZoom + 0.5
      }

      this.$refs.zoomer.manualZoom(this.initialScale) // RESET ZOOMER INITIAL SCALE TO AVOID SCALE RETURN 0 WHEN TOGGLE FULL SCREEN
      this.resizeChart()
    },
    next () {
      if (this.saving) return

      this.saving = true

      this.assignSeatsToTickets()
    },
    async assignSeatsToTickets () {
      this.order.setSeatSelection(this.showing, [...this.selectedSeats].sort(sortAlphaNum), this.ticketTypeIds)

      const remainingOrderItemForSeatSelection = _.find(this.order.showingTicketOrderItems(this.showing), orderItem => !orderItem?.ticket?.seatName)
      if (remainingOrderItemForSeatSelection) {
        await this.seatChartForShowing.fetch(true)

        this.ticket = remainingOrderItemForSeatSelection.ticket
        this.selectedSeats = []
        this.$refs.seatChart?.clearSelection()
        await this.$nextTick()
        if (this.currentSite.autoSelectSeats) {
          this.$refs.seatChart?.autoSelect()
        }
        this.resizeChart()
        this.saving = false
        return
      }
      const { errors } = await this.order.save()

      if (errors && errors.length) {
        this.$mh.showErrors(errors)
        this.order.clearSeatSelection(this.showing)
        this.selectedSeats = []
        this.$refs.seatChart?.clearSelection()
        await this.seatChartForShowing.fetch(true)
        this.saving = false
      } else {
        this.saving = false
        this.$emit('next')
      }
    }
  },
  trackPage () {
    return {
      showingId: (this.order?.showing && this.order.showing.id) || undefined,
      // Append additional tracking metadata for the checkout flow
      ...this.checkoutData.trackingData
    }
  }
}
</script>
<style lang="scss">
.q-dialog.default-background > .q-dialog__inner {
  background-color: var(--q-background);
}

</style>
